import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider } from "@/context/AuthContext";
import { RequireAuth, RequireAdmin } from "@/components/portal/Guards";

import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Home from "@/pages/Home";
import About from "@/pages/About";
import { HomeLoan, LoanAgainstProperty, BusinessLoan, PersonalLoan, WorkingCapital } from "@/pages/LoanPages";
import EMICalcPage from "@/pages/EMICalcPage";
import EligibilityCalculator from "@/pages/EligibilityCalculator";
import LoanComparisonPage from "@/pages/LoanComparisonPage";
import ChargesCalculatorPage from "@/pages/ChargesCalculatorPage";
import Careers from "@/pages/Careers";
import BecomePartner from "@/pages/BecomePartner";
import Login from "@/pages/Login";
import ApplyNow from "@/pages/ApplyNow";
import DocumentsRequired from "@/pages/DocumentsRequired";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Investments from "@/pages/Investments";
import Team from "@/pages/Team";
import WhyFinirio from "@/pages/WhyFinirio";
import HomeLoanGuide from "@/pages/HomeLoanGuide";

import PortalLogin from "@/pages/portal/PortalLogin";
import PortalRegister from "@/pages/portal/PortalRegister";
import PortalLayout from "@/pages/portal/PortalLayout";
import PortalDashboard from "@/pages/portal/PortalDashboard";
import PortalPageView from "@/pages/portal/PortalPageView";
import PortalProfile from "@/pages/portal/PortalProfile";
import AdminUsers from "@/pages/portal/admin/AdminUsers";
import AdminPages from "@/pages/portal/admin/AdminPages";
import AdminLeads from "@/pages/portal/admin/AdminLeads";
import AdminCustomerDetails from "@/pages/portal/admin/AdminCustomerDetails";
import CustomerDetails from "@/pages/CustomerDetails";
import AdminAssessmentDashboard from "@/pages/portal/admin/AdminAssessmentDashboard";
import AdminAssessmentCandidates from "@/pages/portal/admin/AdminAssessmentCandidates";
import AdminAssessmentCandidateDetail from "@/pages/portal/admin/AdminAssessmentCandidateDetail";
import AdminAssessmentQuestions from "@/pages/portal/admin/AdminAssessmentQuestions";
import AdminAssessmentConfig from "@/pages/portal/admin/AdminAssessmentConfig";

import AssessmentRegister from "@/pages/assessment/AssessmentRegister";
import AssessmentRules from "@/pages/assessment/AssessmentRules";
import AssessmentTest from "@/pages/assessment/AssessmentTest";
import AssessmentResult from "@/pages/assessment/AssessmentResult";
import VersionChecker from "@/components/VersionChecker";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname]);
  return null;
};

const Placeholder = ({ title }) => (
  <main className="max-w-[1000px] mx-auto px-4 py-24 text-center">
    <h1 className="text-3xl font-semibold text-slate-800 mb-3">{title}</h1>
    <p className="text-slate-600">This page is coming soon.</p>
  </main>
);

const PublicLayout = ({ children }) => (
  <>
    <Header />
    {children}
    <Footer />
  </>
);

function App() {
  return (
    <div className="App bg-white text-slate-800">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <VersionChecker />
          <Routes>
            {/* Portal (authenticated) routes */}
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/portal/register" element={<PortalRegister />} />
            <Route
              path="/portal"
              element={
                <RequireAuth>
                  <PortalLayout />
                </RequireAuth>
              }
            >
              <Route index element={<PortalDashboard />} />
              <Route path="profile" element={<PortalProfile />} />
              <Route path="pages/:slug" element={<PortalPageView />} />
              <Route
                path="admin/users"
                element={
                  <RequireAdmin>
                    <AdminUsers />
                  </RequireAdmin>
                }
              />
              <Route
                path="admin/pages"
                element={
                  <RequireAdmin>
                    <AdminPages />
                  </RequireAdmin>
                }
              />
              <Route
                path="admin/leads"
                element={
                  <RequireAdmin>
                    <AdminLeads />
                  </RequireAdmin>
                }
              />
              <Route path="admin/customer-details" element={<RequireAdmin><AdminCustomerDetails /></RequireAdmin>} />
              <Route path="admin/assessment" element={<RequireAdmin><AdminAssessmentDashboard /></RequireAdmin>} />
              <Route path="admin/assessment/candidates" element={<RequireAdmin><AdminAssessmentCandidates /></RequireAdmin>} />
              <Route path="admin/assessment/candidates/:id" element={<RequireAdmin><AdminAssessmentCandidateDetail /></RequireAdmin>} />
              <Route path="admin/assessment/questions" element={<RequireAdmin><AdminAssessmentQuestions /></RequireAdmin>} />
              <Route path="admin/assessment/config" element={<RequireAdmin><AdminAssessmentConfig /></RequireAdmin>} />
            </Route>

            {/* Assessment — standalone (no site header/footer) */}
            <Route path="/assessment/test" element={<AssessmentTest />} />
            <Route path="/customer-details" element={<CustomerDetails />} />

            {/* Public marketing website routes */}
            <Route path="/*" element={
              <PublicLayout>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/about-us" element={<About />} />
                  <Route path="/home-loan" element={<HomeLoan />} />
                  <Route path="/loan-against-property" element={<LoanAgainstProperty />} />
                  <Route path="/business-loan" element={<BusinessLoan />} />
                  <Route path="/personal-loan" element={<PersonalLoan />} />
                  <Route path="/working-capital" element={<WorkingCapital />} />
                  <Route path="/why-finirio" element={<WhyFinirio />} />
                  <Route path="/home-loan-guide-mumbai" element={<HomeLoanGuide />} />
                  <Route path="/emi-calculator" element={<EMICalcPage />} />
                  <Route path="/eligibility-calculator" element={<EligibilityCalculator />} />
                  <Route path="/loan-comparison" element={<LoanComparisonPage />} />
                  <Route path="/charges-calculator" element={<ChargesCalculatorPage />} />
                  <Route path="/careers" element={<Careers />} />
                  <Route path="/assessment" element={<AssessmentRegister />} />
                  <Route path="/assessment/rules" element={<AssessmentRules />} />
                  <Route path="/assessment/result" element={<AssessmentResult />} />
                  <Route path="/become-partner" element={<BecomePartner />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/apply-now-cbi" element={<ApplyNow />} />
                  <Route path="/documents" element={<DocumentsRequired />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/investments" element={<Investments />} />
                  <Route path="/team" element={<Team />} />
                  <Route path="*" element={<Placeholder title="Page not found" />} />
                </Routes>
              </PublicLayout>
            } />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
